import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Loader2,
  XCircle,
  ShoppingCart,
  User,
  Calendar,
  DollarSign,
  MessageSquare,
  AlertTriangle,
} from "lucide-react";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  getSaleChangeRequestReasons,
  createCancelRequest,
  createCancelPreSaleRequest,
} from "@/Api/saleChangeRequests";
import { Mask } from "@/utils/mask";
import { usePermissions } from "@/hooks/usePermissions";
import { useSettings } from "@/hooks/useSettings";
import { getSaleChangeRequestWindow } from "@/utils/saleChangeRequestWindow";

interface Reason {
  id: number;
  name: string;
  type: string;
}

interface Sale {
  id: number;
  uuid: string;
  total: number | string;
  client?: {
    name: string;
  };
  created_at: string;
}

interface CancelRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  sale: Sale | null;
  onSuccess?: () => void;
  /** Quando true, envia a solicitação de cancelamento de pré-venda pendente
   * (aprovação rejeita a pré-venda, sem mexer em estoque) em vez do
   * cancelamento de venda normal. */
  isPreSale?: boolean;
}

export function CancelRequestModal({
  isOpen,
  onClose,
  sale,
  onSuccess,
  isPreSale = false,
}: CancelRequestModalProps) {
  const [reasons, setReasons] = useState<Reason[]>([]);
  const [selectedReason, setSelectedReason] = useState<string>("");
  const [otherReason, setOtherReason] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingReasons, setIsLoadingReasons] = useState(false);
  const { isAdmin, isTenantUser } = usePermissions();

  // Garante que as configurações do sistema estejam carregadas ao abrir o modal
  useSettings();

  /**
   * Prazo para solicitar o cancelamento, definido pelo admin em Configurações
   * do Sistema. Admins são isentos, como no backend.
   */
  const requestWindow = getSaleChangeRequestWindow(
    "cancel",
    sale?.created_at,
    isAdmin() || isTenantUser()
  );

  useEffect(() => {
    const fetchReasons = async () => {
      if (!isOpen) return;

      setIsLoadingReasons(true);
      try {
        const { data } = await getSaleChangeRequestReasons("cancel");
        if (data?.response) {
          setReasons(data.response);
        }
      } catch (error) {
        console.error("Erro ao buscar motivos:", error);
      } finally {
        setIsLoadingReasons(false);
      }
    };

    fetchReasons();
  }, [isOpen]);

  const handleClose = () => {
    setSelectedReason("");
    setOtherReason("");
    onClose();
  };

  const handleSubmit = async () => {
    if (!sale) return;

    if (!requestWindow.allowed) {
      toast.error(requestWindow.message ?? "O prazo para esta solicitação expirou");
      return;
    }

    const selectedReasonObj = reasons.find(
      (r) => r.id.toString() === selectedReason
    );
    const isOtherReason = selectedReasonObj?.name === "Outro";

    if (!selectedReason) {
      toast.error("Selecione um motivo para a solicitação");
      return;
    }

    if (isOtherReason && !otherReason.trim()) {
      toast.error("Descreva o motivo da solicitação");
      return;
    }

    setIsLoading(true);
    try {
      const createRequest = isPreSale ? createCancelPreSaleRequest : createCancelRequest;
      const response = await createRequest({
        sale_uuid: sale.uuid,
        reason_id: parseInt(selectedReason),
        reason_other: isOtherReason ? otherReason.trim() : undefined,
      });

      if (response.ok) {
        toast.success("Solicitação de cancelamento enviada com sucesso!");
        handleClose();
        if (onSuccess) {
          onSuccess();
        }
      } else {
        toast.error(response.data?.message || "Erro ao enviar solicitação");
      }
    } catch (error: any) {
      toast.error(error?.message || "Erro ao enviar solicitação");
    } finally {
      setIsLoading(false);
    }
  };

  const selectedReasonObj = reasons.find(
    (r) => r.id.toString() === selectedReason
  );
  const showOtherField = selectedReasonObj?.name === "Outro";

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto p-0">
        {/* Header com gradiente vermelho */}
        <DialogHeader className="bg-gradient-to-r from-red-600 to-red-700 p-6 rounded-t-lg">
          <DialogTitle className="flex items-center gap-3 text-white text-xl">
            <div className="p-2 bg-white/20 rounded-lg">
              <XCircle className="h-5 w-5" />
            </div>
            {isPreSale ? "Solicitar Cancelamento da Pré-Venda" : "Solicitar Cancelamento"}
          </DialogTitle>
          <p className="text-red-100 text-sm mt-1">
            {isPreSale
              ? "Sua solicitação será analisada por um gerente ou administrador antes da pré-venda ser rejeitada"
              : "Sua solicitação será analisada por um gerente ou administrador"}
          </p>
        </DialogHeader>

        {isLoadingReasons ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-red-600" />
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Aviso de prazo expirado */}
            {!requestWindow.allowed && (
              <div className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
                <div>
                  <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                    Prazo para solicitar cancelamento atingido
                  </p>
                  <p className="mt-1 text-sm text-amber-800 dark:text-amber-300">
                    {requestWindow.message}
                  </p>
                </div>
              </div>
            )}

            {/* Card de Informações da Venda */}
            {sale && (
              <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 rounded-xl p-5 border border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-2 mb-4">
                  <ShoppingCart className="h-4 w-4 text-slate-500" />
                  <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
                    Informações da Venda
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-white dark:bg-slate-800 rounded-lg p-3 shadow-sm">
                    <p className="text-xs text-slate-500 mb-1">Venda</p>
                    <p className="font-bold text-lg">#{sale.id}</p>
                  </div>
                  <div className="bg-white dark:bg-slate-800 rounded-lg p-3 shadow-sm">
                    <p className="text-xs text-slate-500 mb-1 flex items-center gap-1">
                      <Calendar className="h-3 w-3" /> Data
                    </p>
                    <p className="font-medium">
                      {new Date(sale.created_at).toLocaleDateString("pt-BR")}
                    </p>
                  </div>
                  <div className="bg-red-50 dark:bg-red-900/30 rounded-lg p-3 shadow-sm border border-red-200 dark:border-red-800">
                    <p className="text-xs text-red-600 dark:text-red-400 mb-1 flex items-center gap-1">
                      <DollarSign className="h-3 w-3" /> Total
                    </p>
                    <p className="font-bold text-lg text-red-700 dark:text-red-300">
                      {Mask.formatCurrency(Number(sale.total))}
                    </p>
                  </div>
                </div>
                <div className="bg-white dark:bg-slate-800 rounded-lg p-3 shadow-sm mt-4">
                  <p className="text-xs text-slate-500 mb-1 flex items-center gap-1">
                    <User className="h-3 w-3" /> Cliente
                  </p>
                  <p className="font-medium">{sale.client?.name || "-"}</p>
                </div>
              </div>
            )}

            {/* Motivo do Cancelamento */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-slate-500" />
                <Label className="text-base font-semibold">Motivo do cancelamento</Label>
                <span className="text-red-500">*</span>
              </div>
              <RadioGroup
                value={selectedReason}
                onValueChange={setSelectedReason}
                className="grid grid-cols-1 gap-2"
              >
                {reasons.map((reason) => (
                  <div
                    key={reason.id}
                    className={`flex items-center space-x-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      selectedReason === reason.id.toString()
                        ? "border-red-500 bg-red-50 dark:bg-red-900/20"
                        : "border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800"
                    }`}
                    onClick={() => setSelectedReason(reason.id.toString())}
                  >
                    <RadioGroupItem
                      value={reason.id.toString()}
                      id={`cancel-reason-${reason.id}`}
                      className="border-2"
                    />
                    <Label
                      htmlFor={`cancel-reason-${reason.id}`}
                      className="flex-1 cursor-pointer font-medium"
                    >
                      {reason.name}
                    </Label>
                  </div>
                ))}
              </RadioGroup>

              {showOtherField && (
                <div className="mt-3 space-y-2">
                  <Label htmlFor="cancel-other-reason" className="text-sm">
                    Descreva o motivo <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="cancel-other-reason"
                    value={otherReason}
                    onChange={(e) => setOtherReason(e.target.value)}
                    placeholder="Descreva detalhadamente o motivo do cancelamento..."
                    className="min-h-[80px] resize-none"
                  />
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="p-6 pt-0 gap-3">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
            className="flex-1 sm:flex-none"
          >
            Voltar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isLoading || !selectedReason || !requestWindow.allowed}
            variant="destructive"
            className="flex-1 sm:flex-none"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Enviando...
              </>
            ) : (
              <>
                <XCircle className="h-4 w-4 mr-2" />
                Solicitar Cancelamento
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
